import React, { useState } from "react";
import { Star, X, Loader } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function RateDriverModal({ booking, onClose, onRated }) {
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!booking) return null;

  const submit = async () => {
    if (stars < 1) {
      setErr("Please pick a star rating");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/bookings/${booking.id}/rate`, { stars, comment });
      onRated?.(data);
      onClose?.();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="rate-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white border-2 border-asap-deep max-w-md w-full shadow-hard">
        <div className="p-5 border-b-2 border-asap-deep bg-asap-yellow flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-asap-deep font-bold">Job Complete</div>
            <h3 className="font-display text-xl uppercase text-asap-deep">Rate your rescuer</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="rate-close-btn"
            className="p-1 border-2 border-asap-deep text-asap-deep hover:bg-asap-deep hover:text-white"
            aria-label="Close"
          >
            <X size={16}/>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Driver</div>
            <div className="font-display text-lg text-asap-deep">{booking.driver_name || "Your rescuer"}</div>
            <div className="text-xs text-slate-500">{booking.service_name} · ${booking.price?.toFixed?.(2)}</div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-asap-deep font-bold mb-2">Your rating</div>
            <div className="flex gap-2" data-testid="star-picker">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hover || stars) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setStars(n)}
                    className={`p-2 border-2 transition ${active ? "bg-asap-yellow border-asap-deep text-asap-deep" : "bg-white border-slate-300 text-slate-300 hover:border-asap-deep"}`}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    <Star size={22} strokeWidth={2.5} fill={active ? "currentColor" : "none"}/>
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] mt-1 text-slate-500">
              {stars === 5 && "Legend — the roadrunner would be proud"}
              {stars === 4 && "Great job"}
              {stars === 3 && "Got the job done"}
              {stars === 2 && "Below expectations"}
              {stars === 1 && "Serious issue — we'll follow up"}
              {stars === 0 && "Tap a star"}
            </div>
          </div>

          <label className="block">
            <div className="text-xs uppercase tracking-widest text-asap-deep font-bold mb-1">Add a comment (optional)</div>
            <textarea
              data-testid="rate-comment"
              rows={3}
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Was quick, friendly, professional…"
              className="w-full px-3 py-2 border-2 border-asap-deep focus:border-asap-orange outline-none text-sm"
            />
            <div className="text-[10px] text-slate-400 mt-0.5 text-right">{comment.length}/500</div>
          </label>

          {err && <div className="text-asap-red text-sm font-bold border-2 border-asap-red bg-red-50 px-3 py-2" data-testid="rate-error">{err}</div>}
        </div>

        <div className="p-5 border-t-2 border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            data-testid="rate-skip-btn"
            className="px-4 py-2 border-2 border-asap-deep text-asap-deep font-bold uppercase text-xs hover:bg-asap-deep hover:text-white transition"
          >
            Skip
          </button>
          <button
            data-testid="rate-submit-btn"
            onClick={submit}
            disabled={busy || stars < 1}
            className="px-5 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-sm border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-40 inline-flex items-center gap-2"
          >
            {busy ? <Loader size={14} className="animate-spin"/> : <Star size={14}/>}
            {busy ? "Sending…" : "Submit Rating"}
          </button>
        </div>
      </div>
    </div>
  );
}
